import { BaseTheme, defaultCssVariables, FeaturesTheme, ThemeType } from 'core/styled';
import { ModuleStyleStyles } from 'services/brand/brand.type';
import produce from 'immer';

const computeBaseTheme = (defaultBaseTheme: BaseTheme, apiTheme?: ModuleStyleStyles): BaseTheme => {
    if (!apiTheme?.is_enabled) {
        return defaultBaseTheme;
    }

    return produce<BaseTheme>(defaultBaseTheme, (draftTheme) => {
        draftTheme.color.backgroundMain = apiTheme.style.backgroundColor; // Top bar
        draftTheme.color.backgroundBar = apiTheme.style.principalColor; // Top bar
        draftTheme.color.backgroundMainButton = apiTheme.style.principalColor; // basket button
        draftTheme.color.borderMainButton = apiTheme.style.principalColor; // border basket button
        draftTheme.color.textMainButton = apiTheme.style.textOnBrandColor; // text basket button
        draftTheme.color.textMain = apiTheme.style.titleColor;
        draftTheme.color.textSecondary = apiTheme.style.textColor;
        draftTheme.color.borderSecondaryButton = apiTheme.style.separatorColor;
        draftTheme.color.backgroundSecondaryButton = apiTheme.style.backgroundColor;
        draftTheme.color.textSecondaryButton = apiTheme.style.textColor;
        draftTheme.color.separator = apiTheme.style.separatorColor;
        draftTheme.background = apiTheme.style.backgroundImage;
    });
};

const computeFeaturesTheme = (
    defaultFeaturesTheme: FeaturesTheme,
    apiTheme?: ModuleStyleStyles,
): FeaturesTheme => {
    if (!apiTheme?.is_enabled) {
        return defaultFeaturesTheme;
    }

    return produce(defaultFeaturesTheme, (draftTheme) => {
        // When api has theme prorerties insert here.
        return draftTheme;
    });
};

const camelToKebabCase = (str: string) =>
    str.replace(/[A-Z]/g, (letter: string) => `-${letter.toLowerCase()}`);

const buildCssTheme = (
    theme: Record<string, unknown>,
    parentKey?: string,
): Array<[string, string]> => {
    return Object.entries(theme).flatMap(([key, value]) => {
        const currentKey = parentKey ? `${parentKey}-${key}` : key;

        if (typeof value === 'string') {
            return [[`--${camelToKebabCase(currentKey)}`, value]];
        }

        return buildCssTheme(value as Record<string, unknown>, currentKey);
    });
};

export const makeCssTheme = (theme: BaseTheme) => Object.fromEntries(buildCssTheme(theme));

export const makeTheme = (apiTheme?: ModuleStyleStyles) => {
    const baseTheme = computeBaseTheme(defaultCssVariables, apiTheme);

    const defaultFeaturesTheme: FeaturesTheme = {
        buttonMain: {
            background: baseTheme.color.backgroundMainButton,
            borderColor: baseTheme.color.backgroundMainButton,
            borderRadius: baseTheme.radius.sm,
            fontSize: baseTheme.font.size.small,
            fontColor: baseTheme.color.textMainButton,
        },
        buttonSecondary: {
            background: baseTheme.color.backgroundSecondaryButton,
            borderColor: baseTheme.color.borderSecondaryButton,
            borderRadius: baseTheme.radius.sm,
            fontSize: baseTheme.font.size.small,
            fontColor: baseTheme.color.textSecondaryButton,
        },
        title: {
            color: baseTheme.color.textMain,
            fontFamily: baseTheme.font.family.title,
            fontSize: baseTheme.font.size.huge,
            lineHeight: baseTheme.font.lineHeight.medium,
            weight: baseTheme.font.weight.bold,
        },
        text: {
            color: baseTheme.color.textSecondary,
            fontFamily: baseTheme.font.family.text,
            fontSize: baseTheme.font.size.normal,
            lineHeight: baseTheme.font.lineHeight.normal,
            weight: baseTheme.font.weight.regular,
        },
    };

    const featuresTheme = computeFeaturesTheme(defaultFeaturesTheme, apiTheme);

    const theme: ThemeType = {
        base: baseTheme,
        feature: featuresTheme,
    };

    return {
        themeCss: makeCssTheme(baseTheme),
        theme,
    };
};
